<template>
	<div class="row q-col-gutter-lg">
		<div id="circularProgressContainer">
			<CircularProgress />			
		</div>
		<div class="col-xs-8" id="pageTitleContainer">
			<div class="text-h4 text-weight-bold" id="pageTitle">{{ $t(title) }}</div>
			<div v-if="description" class="q-py-xs" id="pageDescription">{{ $t(description) }}</div>
		</div>
	</div>
	<q-separator class="q-my-md" id="separator" />
	<div v-if="groups" id="pageContent">
		<q-card flat bordered v-for="group in groups" :key="group" id="groupContainer">
			<q-card-section class="group-heading" v-if="group">
				<div id="groupTitle" class="text-h6">{{ $t(group) }}</div>
			</q-card-section>
			<q-card-section id="visitorDetailsContainer" class="group-body">
				<q-form ref="visitorDetailsForm" class="details-container" v-for="field in fields" :key="field.cdfId" id="visitorDetailsForm" @submit.prevent="">
					<!-- 
						typeId: 1, type: 'Text'
							subTypeId: 1, subType: 'None'
						typeId: 2, type: 'TextList'
							subTypeId: 4, subType: 'RadioButton'
							subTypeId: 5, subType: 'DropDownList'
							subTypeId: 6, subType: 'ComboBox'
							subTypeId: 7, subType: 'MultiSelect'
							subTypeId: 10, subType: '' //Dropdown list with dynamic query
						typeId: 3, type: 'Numeric'
							subTypeId: 1, subType: 'None'
						typeId: 4, type: 'Image' // Not supported?
						typeId: 5, type: 'DateTime'
						typeId: 6, type: 'Boolean' // checkbox												
					-->

					<!-- type = Text, subType = None -->
					<div v-if="field.groupName === group && field.typeId === 1" id="textField">
						<q-input
							v-model="field.originalValue"
							:label="$t(field.displayValue)"
							:reactive-rules="true"
							:rules="field.mandatory && !field.readOnly ? [val => !!val || $t('portal_requiredMsg')] : undefined"
							stack-label
							filled
							hide-bottom-space
							id="visitorDetailsInput"
							:class="{ required: field.mandatory }"
							:disable="field.readOnly"/>
					</div>
					

					<!-- type = Numeric, subType = None -->
					<div v-if="field.groupName === group && field.typeId === 3" id="numericField">
						<q-input							
							v-model="field.originalValue"
							:label="$t(field.displayValue)"
							:reactive-rules="true"
							:rules="field.mandatory && !field.readOnly ? [val => !!val || $t('portal_requiredMsg')] : undefined"
							type="number"
							stack-label
							filled
							hide-bottom-space
							id="visitorDetailsInput"
							:class="{ required: field.mandatory }"
							:disable="field.readOnly"/>
					</div>

					<!-- type = Date -->
					<div id="dateField" v-if="field.groupName === group && field.typeId === 5">
						<q-input 
							filled 
							v-model="field.originalValue" 
							stack-label
							:label="$t(field.displayValue)"
							:disable="field.readOnly"
							:reactive-rules="true"
							:rules="field.mandatory && !field.readOnly ? 
								[validateRequiredDate] : !field.readOnly ? [validateOptionalDate] : undefined"
							hide-bottom-space
							id="visitorDetailsInput"
							:class="{ required: field.mandatory }">
							<template v-slot:append>
								<q-icon name="event" class="cursor-pointer" v-if="!field.readOnly">
									<q-popup-proxy cover transition-show="scale" transition-hide="scale">
										<q-date type="date" v-model="field.originalValue" 
											:value="field.originalValue"
											minimal
											mask="DD-MMM-YYYY"
											id="visitorDetailsInputDate">
											<div class="row items-center justify-end">
												<q-btn v-close-popup :label="$t('portal_close')" color="primary" flat />
											</div>
										</q-date>
									</q-popup-proxy>
								</q-icon>
							</template>
						</q-input>
					</div>

					<!-- type = TextList, subType = RadioButton -->
					<div id="radioButtonField" v-if="field.groupName === group && field.typeId === 2 && field.subTypeId === 4">
						<q-field							
							stack-label
							:label="$t(field.displayValue)"
							v-model="field.originalValue"
							filled
							:reactive-rules="true"					
							:rules="field.mandatory && !field.readOnly ? [val => !!val || $t('portal_requiredMsg')] : undefined"
							hide-bottom-space
							id="visitorDetailsInput"
							:class="{ required: field.mandatory }"
							:disable="field.readOnly"
						>
							<template v-slot:control>
								<q-option-group		
									:options="portal.getSelectOptions(field.listOptions)"
									type="radio"
									v-model="field.originalValue"
									id="visitorDetailsInputOptionGroup"
									:disable="field.readOnly"/>
							</template>
						</q-field>
					</div>

					<!-- type = TextList, subType = DropDownList -->
					<div id="selectField" v-if="field.groupName === group && field.typeId === 2 && field.subTypeId === 5">
						<div class="selectFieldLabel">{{$t(field.displayValue)}}</div>
						<q-select
							filled 
							stack-label
							emit-value
							v-model="field.originalValue" 
							:options="portal.getSelectOptions(field.listOptions)" 
							:display-value="field.originalValue ? portal._translate(field.originalValue.replace(/ /g, '-').toLowerCase()) : ''"
							:label="$t(field.displayValue)"
							:reactive-rules="true"
							:rules="field.mandatory && !field.readOnly ? [val => !!val || $t('portal_requiredMsg')] : undefined"
							hide-bottom-space 
							id="visitorDetailsInput"
							:class="{ required: field.mandatory }"
							:disable="field.readOnly"/>
					</div>

					<!-- type = TextList, subType = Multiselect -->
					<div id="multiselectField" v-if="field.groupName === group && field.typeId === 2 && field.subTypeId === 7">
						<q-field							
							stack-label
							:label="$t(field.displayValue)"
							v-model="field.originalValue"
							filled
							:reactive-rules="true"
							:rules="field.mandatory && !field.readOnly ? [val => val.length || $t('portal_requiredMsg')] : undefined"
							hide-bottom-space
							id="visitorDetailsInput"
							:class="{ required: field.mandatory }"
							:disable="field.readOnly">
							<template v-slot:control>
								<q-option-group		
									:options="portal.getSelectOptions(field.listOptions)"
									type="checkbox"
									v-model="field.originalValue"
									id="visitorDetailsInputOptionGroup"
									:disable="field.readOnly"
								/>
							</template>
						</q-field>
					</div>

					<!-- type = Boolean, subType = None -->
					<div id="checkboxField" v-if="field.groupName === group && field.typeId === 6 && field.subTypeId === 1">
						<q-field							
							filled
							stack-label
							id="visitorDetailsInput"
							:class="{ required: field.mandatory }"
							:disable="field.readOnly">
							<template v-slot:control>
								<q-checkbox v-model="field.originalValue" true-value="on" false-value="off" :label="$t(field.displayValue)" :disable="field.readOnly"/>
							</template>
						</q-field>
					</div>
				</q-form>
			</q-card-section>
		</q-card>
	</div>
</template>

<style lang="less" scoped src="./visitor-details.less"></style>
<script lang="ts" src="./visitor-details.ts"></script>
